import { useState, useEffect, useRef, useCallback } from "react";
import {
  Cpu, MemoryStick, HardDrive, Database, Activity,
  RefreshCw, AlertTriangle, CheckCircle2, Clock, ScrollText,
  ChevronDown, ChevronUp, Server, Bell, BellOff, Settings, Trash2,
  Loader2, Gauge, Image, Layers, Zap, Timer, XCircle, FileText, X,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { adminFetch } from "./adminUtils";

function GaugeRing({ percent, color, size = 100, strokeWidth = 8 }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="currentColor" className="text-foreground" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" className="transition-all duration-700" />
    </svg>
  );
}

function MetricCard({ icon: Icon, title, value, subtitle, accent, children, testId }) {
  return (
    <div data-testid={testId}
      className="bg-slate-900 border border-slate-800 rounded-xl p-5 hover:border-slate-700 transition-colors">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-slate-400 text-xs uppercase tracking-wide">{title}</p>
          <p className="text-white text-lg font-bold leading-tight">{value}</p>
        </div>
      </div>
      {subtitle && <p className="text-muted-foreground text-xs">{subtitle}</p>}
      {children}
    </div>
  );
}

function UsageBar({ label, used, total, unit, color }) {
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  return (
    <div className="mt-3 space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-slate-400 font-medium">{used} / {total} {unit}</span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function LogViewer({ logs }) {
  const [expandedFile, setExpandedFile] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (expandedFile !== null && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [expandedFile, logs]);  // Added 'logs' dependency

  if (!logs || logs.length === 0) {
    return <p className="text-muted-foreground text-sm">No hay logs disponibles</p>;
  }

  return (
    <div className="space-y-3">
      {logs.map((logFile, idx) => (
        <div key={logFile.file} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <button
            data-testid={`log-toggle-${logFile.file}`}
            onClick={() => setExpandedFile(expandedFile === idx ? null : idx)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-muted-foreground" />
              <span className="text-slate-300 text-sm font-medium">{logFile.file}</span>
              {logFile.lines && (
                <span className="text-muted-foreground text-xs">({logFile.lines.length} lineas)</span>
              )}
            </div>
            {expandedFile === idx
              ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
              : <ChevronDown className="w-4 h-4 text-muted-foreground" />
            }
          </button>
          {expandedFile === idx && (
            <div ref={scrollRef}
              className="max-h-80 overflow-y-auto border-t border-slate-800 bg-slate-950 p-3 font-mono text-xs">
              {logFile.error ? (
                <p className="text-red-400">{logFile.error}</p>
              ) : logFile.lines?.length > 0 ? (
                logFile.lines.map((line, i) => (
                  <div key={`line-${i}-${line.substring(0, 30)}`} className={`py-0.5 ${
                    line.toLowerCase().includes("error") ? "text-red-400" :
                    line.toLowerCase().includes("warning") ? "text-amber-400" :
                    "text-muted-foreground"
                  }`}>
                    <span className="text-foreground mr-2 select-none">{i + 1}</span>
                    {line}
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground">Archivo vacio</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminSystemMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);

  // Job tracking state
  const [jobStats, setJobStats] = useState(null);
  const [cleaningJobs, setCleaningJobs] = useState(false);

  // Alert config state
  const [alertConfig, setAlertConfig] = useState(null);
  const [alertHistory, setAlertHistory] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configDraft, setConfigDraft] = useState({});
  const [clearingAlerts, setClearingAlerts] = useState(false);

  // Severity detail drawer
  const [sevDrawer, setSevDrawer] = useState(null); // { level, lines, total, loading }
  // Metric history modal
  const [metricModal, setMetricModal] = useState(null); // "cpu" | "memory" | "disk" | null

  const openSeverityDetail = async (level) => {
    setSevDrawer({ level, lines: [], total: 0, loading: true });
    try {
      const res = await adminFetch(`/api/admin/logs/by-severity?level=${level}&limit=200`);
      setSevDrawer({ level, lines: res.lines || [], total: res.total || 0, loading: false });
    } catch (e) {
      console.warn("[logs/severity]", e.message);
      setSevDrawer((d) => d && { ...d, loading: false });
    }
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const result = await adminFetch("/api/admin/system-status");
      setData(result);
    } catch (err) {
      toast.error("Error cargando estado del sistema: " + err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadJobStats = useCallback(async () => {
    try {
      const result = await adminFetch("/api/admin/job-tracking-stats");
      setJobStats(result);
    } catch (err) {
      console.error("Error loading job stats:", err);
    }
  }, []);  // adminFetch is stable, no need to include

  const cleanupStuckJobs = useCallback(async () => {
    if (!window.confirm("¿Marcar todos los jobs stuck como fallidos?")) return;
    
    setCleaningJobs(true);
    try {
      const result = await adminFetch("/api/admin/cleanup-stuck-jobs", { method: "POST" });
      toast.success(`${result.cleaned.image_jobs + result.cleaned.recipe_jobs} jobs limpiados`);
      await loadJobStats(); // Reload stats
    } catch (err) {
      toast.error("Error limpiando jobs: " + err.message);
    } finally {
      setCleaningJobs(false);
    }
  }, [loadJobStats]);  // Added loadJobStats dependency

  const loadAlertConfig = useCallback(async () => {
    try {
      const cfg = await adminFetch("/api/admin/alerts/config");
      setAlertConfig(cfg);
      setConfigDraft({ cpu_percent: cfg.cpu_percent, memory_percent: cfg.memory_percent, disk_percent: cfg.disk_percent });
    } catch (e) { console.warn("[alerts/config]", e.message); }
  }, []);

  const loadAlertHistory = useCallback(async () => {
    try {
      const res = await adminFetch("/api/admin/alerts/history?limit=20");
      setAlertHistory(res.alerts || []);
    } catch (e) { console.warn("[alerts/config]", e.message); }
  }, []);

  const saveConfig = async (overrides = {}) => {
    setSavingConfig(true);
    try {
      const body = { ...configDraft, ...overrides };
      const res = await adminFetch("/api/admin/alerts/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setAlertConfig(res);
      setConfigDraft({ cpu_percent: res.cpu_percent, memory_percent: res.memory_percent, disk_percent: res.disk_percent });
      toast.success("Configuracion de alertas guardada");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleAlerts = () => {
    const next = !alertConfig?.enabled;
    saveConfig({ enabled: next });
  };

  const clearAlerts = async () => {
    setClearingAlerts(true);
    try {
      await adminFetch("/api/admin/alerts/history", { method: "DELETE" });
      setAlertHistory([]);
      toast.success("Historial de alertas limpiado");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClearingAlerts(false);
    }
  };

  useEffect(() => { load(); loadAlertConfig(); loadAlertHistory(); loadJobStats(); }, [load, loadAlertConfig, loadAlertHistory, loadJobStats]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        load(true);
        loadJobStats();
      }, 10000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, load, loadJobStats]);

  const getStatusColor = (value, warnThreshold, critThreshold) => {
    if (!data) return "#475569";
    if (value > critThreshold) return "#ef4444";
    if (value > warnThreshold) return "#f59e0b";
    return "#22c55e";
  };
  const cpuColor = getStatusColor(data?.cpu?.percent, 50, 80);
  const memColor = getStatusColor(data?.memory?.percent, 60, 85);
  const diskColor = getStatusColor(data?.disk?.percent, 70, 90);

  const formatUptime = (seconds) => {
    if (!seconds) return "—";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  if (loading && !data) {
    return (
      <div className="p-6 lg:p-8" data-testid="system-monitor-loading">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-cyan-500/15 rounded-xl flex items-center justify-center">
            <Activity className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">Monitorizacion del Sistema</h1>
            <p className="text-muted-foreground text-sm">Cargando metricas...</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={`loading-${i}`} className="bg-slate-900 border border-slate-800 rounded-xl p-5 h-40 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="system-monitor-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-500/15 rounded-xl flex items-center justify-center">
            <Activity className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">Monitorizacion del Sistema</h1>
            <p className="text-muted-foreground text-sm">
              {data?.timestamp ? `Ultima actualizacion: ${new Date(data.timestamp).toLocaleTimeString("es-ES")}` : "—"}
              {data?.metrics_source && (
                <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  data.metrics_source === "container"
                    ? "bg-emerald-500/15 text-emerald-400"
                    : "bg-amber-500/15 text-amber-400"
                }`}>
                  {data.metrics_source === "container" ? "Contenedor" : "Host"}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="auto-refresh-toggle"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              autoRefresh
                ? "bg-cyan-500/15 text-cyan-400 border-cyan-500/30"
                : "bg-slate-800 text-muted-foreground border-slate-700 hover:text-slate-300"
            }`}>
            <Clock className="w-3 h-3" />
            Auto {autoRefresh ? "ON" : "OFF"}
          </button>
          <Button
            data-testid="refresh-system-btn"
            onClick={() => load(true)}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="border-slate-700 text-slate-400 hover:text-white"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Gauges row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* CPU */}
        <div data-testid="cpu-gauge" onClick={() => setMetricModal("cpu")}
          className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex items-center gap-5 cursor-pointer hover:border-slate-600 transition-colors group">
          <div className="relative flex-shrink-0">
            <GaugeRing percent={data?.cpu?.percent || 0} color={cpuColor} size={80} strokeWidth={7} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-bold text-sm">{data?.cpu?.percent || 0}%</span>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Cpu className="w-4 h-4 text-muted-foreground" />
              <span className="text-slate-300 text-sm font-medium">CPU</span>
            </div>
            <p className="text-muted-foreground text-xs">{data?.cpu?.cores || 0} cores</p>
            <p className="text-foreground text-[10px] mt-1 group-hover:text-slate-500 transition-colors">Ver historial →</p>
          </div>
        </div>

        {/* Memory */}
        <div data-testid="memory-gauge" onClick={() => setMetricModal("memory")}
          className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex items-center gap-5 cursor-pointer hover:border-slate-600 transition-colors group">
          <div className="relative flex-shrink-0">
            <GaugeRing percent={data?.memory?.percent || 0} color={memColor} size={80} strokeWidth={7} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-bold text-sm">{data?.memory?.percent || 0}%</span>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MemoryStick className="w-4 h-4 text-muted-foreground" />
              <span className="text-slate-300 text-sm font-medium">Memoria</span>
            </div>
            <p className="text-muted-foreground text-xs">
              {data?.memory?.used_gb || 0} / {data?.memory?.total_gb || 0} GB
            </p>
            <p className="text-foreground text-[10px] mt-1 group-hover:text-slate-500 transition-colors">Ver historial →</p>
          </div>
        </div>

        {/* Disk */}
        <div data-testid="disk-gauge" onClick={() => setMetricModal("disk")}
          className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex items-center gap-5 cursor-pointer hover:border-slate-600 transition-colors group">
          <div className="relative flex-shrink-0">
            <GaugeRing percent={data?.disk?.percent || 0} color={diskColor} size={80} strokeWidth={7} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-bold text-sm">{data?.disk?.percent || 0}%</span>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <HardDrive className="w-4 h-4 text-muted-foreground" />
              <span className="text-slate-300 text-sm font-medium">Disco</span>
            </div>
            <p className="text-muted-foreground text-xs">
              {data?.disk?.used_gb || 0} / {data?.disk?.total_gb || 0} GB
            </p>
            <p className="text-foreground text-[10px] mt-1 group-hover:text-slate-500 transition-colors">Ver historial →</p>
          </div>
        </div>
      </div>

      {/* Metric History Modal */}
      {metricModal && (
        <MetricHistoryModal metric={metricModal} onClose={() => setMetricModal(null)} />
      )}

      {/* Detail cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        {/* Database */}
        <MetricCard
          icon={Database}
          title="Base de Datos"
          value={data?.database?.connected ? "Conectada" : "Desconectada"}
          accent={data?.database?.connected ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}
          testId="db-status-card"
        >
          {data?.database?.connected && (
            <div className="mt-3 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Nombre</span>
                <span className="text-slate-300 font-mono">{data.database.db_name}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Colecciones</span>
                <span className="text-slate-300">{data.database.collections}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Objetos</span>
                <span className="text-slate-300">{data.database.objects?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Datos</span>
                <span className="text-slate-300">{data.database.data_size_mb} MB</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Almacenamiento</span>
                <span className="text-slate-300">{data.database.storage_size_mb} MB</span>
              </div>
            </div>
          )}
          {data?.database?.error && (
            <p className="mt-2 text-red-400 text-xs">{data.database.error}</p>
          )}
        </MetricCard>

        {/* Process */}
        <MetricCard
          icon={Server}
          title="Proceso Backend"
          value={`PID ${data?.process?.pid || "—"}`}
          subtitle={`Uptime: ${formatUptime(data?.process?.uptime_seconds)}`}
          accent="bg-violet-500/15 text-violet-400"
          testId="process-card"
        >
          <div className="mt-3 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Memoria</span>
              <span className="text-slate-300">{data?.process?.memory_mb || 0} MB</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">CPU</span>
              <span className="text-slate-300">{data?.process?.cpu_percent || 0}%</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Threads</span>
              <span className="text-slate-300">{data?.process?.threads || 0}</span>
            </div>
          </div>
        </MetricCard>

        {/* Status summary */}
        <MetricCard
          icon={Activity}
          title="Estado General"
          value={
            data?.database?.connected && (data?.cpu?.percent || 0) < 90 && (data?.memory?.percent || 0) < 95
            && (data?.event_loop_latency_ms || 0) < 50 && (!data?.bottlenecks || data.bottlenecks.length === 0)
              ? "Saludable"
              : data?.bottlenecks?.some(b => b.severity === "critical")
              ? "Critico"
              : "Alerta"
          }
          accent={
            data?.database?.connected && (data?.cpu?.percent || 0) < 90 && (data?.memory?.percent || 0) < 95
            && (data?.event_loop_latency_ms || 0) < 50 && (!data?.bottlenecks || data.bottlenecks.length === 0)
              ? "bg-emerald-500/15 text-emerald-400"
              : data?.bottlenecks?.some(b => b.severity === "critical")
              ? "bg-red-500/15 text-red-400"
              : "bg-amber-500/15 text-amber-400"
          }
          testId="health-card"
        >
          <div className="mt-3 space-y-2">
            <StatusItem label="MongoDB" ok={data?.database?.connected} />
            <StatusItem label="CPU < 90%" ok={(data?.cpu?.percent || 0) < 90} />
            <StatusItem label="Memoria < 95%" ok={(data?.memory?.percent || 0) < 95} />
            <StatusItem label="Disco < 95%" ok={(data?.disk?.percent || 0) < 95} />
            <StatusItem label="Event Loop < 50ms" ok={(data?.event_loop_latency_ms || 0) < 50} />
            <StatusItem label="Object Storage" ok={data?.image_storage?.cloud?.connected} />
            <StatusItem label="Sin cuellos de botella" ok={!data?.bottlenecks || data.bottlenecks.length === 0} />
          </div>
        </MetricCard>
      </div>

      {/* ══════════ BOTTLENECK ALERTS ══════════ */}
      {data?.bottlenecks && data.bottlenecks.length > 0 && (
        <div className="mb-6" data-testid="bottleneck-alerts">
          <h2 className="text-white text-base font-semibold mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            Cuellos de Botella Detectados
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">
              {data.bottlenecks.length}
            </span>
          </h2>
          <div className="space-y-2">
            {data.bottlenecks.map((b, i) => (
              <div key={`bn-${b.area}-${b.severity}-${i}`} data-testid={`bottleneck-${i}`}
                className={`flex items-start gap-3 p-4 rounded-xl border ${
                  b.severity === "critical"
                    ? "bg-red-500/5 border-red-500/30"
                    : b.severity === "warning"
                    ? "bg-amber-500/5 border-amber-500/30"
                    : "bg-sky-500/5 border-sky-500/30"
                }`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  b.severity === "critical"
                    ? "bg-red-500/15 text-red-400"
                    : b.severity === "warning"
                    ? "bg-amber-500/15 text-amber-400"
                    : "bg-sky-500/15 text-sky-400"
                }`}>
                  {b.severity === "critical" ? <XCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                      b.severity === "critical" ? "text-red-400 bg-red-500/10" : "text-amber-400 bg-amber-500/10"
                    }`}>{b.severity}</span>
                    <span className="text-muted-foreground text-[10px] uppercase">{b.area?.replace("_", " ")}</span>
                  </div>
                  <p className="text-slate-300 text-sm">{b.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════ BACKGROUND WORKERS MONITOR ══════════ */}
      <div className="mb-6" data-testid="workers-monitor">
        <h2 className="text-white text-base font-semibold mb-3 flex items-center gap-2">
          <Layers className="w-4 h-4 text-muted-foreground" />
          Workers en Background
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data?.background_workers && Object.entries(data.background_workers).map(([lane, m]) => {
            const total = (m.completed || 0) + (m.failed || 0);
            const failRate = total > 0 ? Math.round((m.failed / total) * 100) : 0;
            const isActive = m.queue_depth > 0;
            const hasFailures = m.failed > 0 || m.timed_out > 0;
            const laneLabels = { critical: "Tiempo Real", io: "Importaciones", ai: "IA (Imágenes/Recetas)" };
            const laneDesc = { critical: "Webhooks, TPV, resolución de platos", io: "CSV, Excel, operaciones masivas", ai: "Generación de imágenes, recetas, enriquecimiento" };
            const laneColors = { critical: "cyan", io: "violet", ai: "amber" };
            const c = laneColors[lane] || "slate";

            return (
              <div key={lane} data-testid={`worker-lane-${lane}`}
                className={`bg-slate-900 border rounded-xl p-5 transition-colors ${
                  isActive ? `border-${c}-500/40 shadow-lg shadow-${c}-500/5` : "border-slate-800"
                }`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${
                      isActive ? `bg-${c}-400 animate-pulse` : "bg-slate-700"
                    }`} />
                    <span className="text-slate-300 text-sm font-semibold">{laneLabels[lane] || lane}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.active_workers > 0 && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">
                        {m.active_workers} worker{m.active_workers !== 1 ? "s" : ""}
                      </span>
                    )}
                    {isActive && (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full bg-${c}-500/15 text-${c}-400`}>
                        {m.queue_depth} en cola
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-muted-foreground text-xs mb-4">{laneDesc[lane] || ""}</p>

                {/* Auto-scaling badge for AI lane */}
                {m.auto_scaling?.enabled && (
                  <div className="mb-4 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-amber-400/80 font-medium">Auto-scaling activo</span>
                      <span className="text-slate-400">
                        {m.active_workers}/{m.auto_scaling.max_workers} max
                      </span>
                    </div>
                    {m.auto_scaling.estimated_drain_s > 0 && (
                      <p className="text-amber-300/70 text-[10px] mt-1.5">
                        Drenaje estimado: ~{m.auto_scaling.estimated_drain_s > 60
                          ? `${Math.round(m.auto_scaling.estimated_drain_s / 60)} min`
                          : `${Math.round(m.auto_scaling.estimated_drain_s)}s`}
                      </p>
                    )}
                  </div>
                )}

                {/* Metrics grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <p className="text-muted-foreground text-[10px] uppercase">Completadas</p>
                    <p className="text-emerald-400 text-lg font-bold">{(m.completed || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <p className="text-muted-foreground text-[10px] uppercase">Fallidas</p>
                    <p className={`text-lg font-bold ${m.failed > 0 ? "text-red-400" : "text-muted-foreground"}`}>
                      {(m.failed || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <p className="text-muted-foreground text-[10px] uppercase">Timeouts</p>
                    <p className={`text-lg font-bold ${m.timed_out > 0 ? "text-amber-400" : "text-muted-foreground"}`}>
                      {m.timed_out || 0}
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <p className="text-muted-foreground text-[10px] uppercase">T. Medio</p>
                    <p className="text-slate-300 text-lg font-bold">
                      {m.avg_duration_s > 60
                        ? `${Math.floor(m.avg_duration_s / 60)}m ${Math.round(m.avg_duration_s % 60)}s`
                        : `${m.avg_duration_s || 0}s`}
                    </p>
                  </div>
                </div>

                {/* Fail rate bar */}
                {total > 0 && (
                  <div className="mt-3 space-y-1">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-muted-foreground">Tasa de éxito</span>
                      <span className={failRate > 20 ? "text-red-400 font-medium" : "text-emerald-400"}>
                        {100 - failRate}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                        style={{ width: `${100 - failRate}%` }} />
                    </div>
                  </div>
                )}

                {hasFailures && (
                  <p className="text-amber-400/70 text-[10px] mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-2.5 h-2.5" />
                    {m.timed_out > 0 ? `${m.timed_out} timeout(s)` : ""}{m.timed_out > 0 && m.failed > 0 ? " · " : ""}{m.failed > 0 ? `${m.failed} error(es)` : ""}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ══════════ AI WORKER (EXTERNAL PROCESS) ══════════ */}
      {data?.ai_worker && (
        <div className="mb-6" data-testid="ai-worker-monitor">
          <h2 className="text-white text-base font-semibold mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            AI Worker (Proceso Externo)
            {(data.ai_worker.queue_depth > 0 || data.ai_worker.running > 0) && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 animate-pulse">
                Activo
              </span>
            )}
          </h2>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <p className="text-muted-foreground text-xs mb-4">
              Proceso independiente que procesa tareas de IA (imagenes y recetas) desde la cola en MongoDB, sin bloquear FastAPI.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-muted-foreground text-[10px] uppercase">En Cola</p>
                <p className={`text-lg font-bold ${data.ai_worker.queue_depth > 0 ? "text-amber-400" : "text-muted-foreground"}`}>
                  {data.ai_worker.queue_depth}
                </p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-muted-foreground text-[10px] uppercase">Procesando</p>
                <p className={`text-lg font-bold ${data.ai_worker.running > 0 ? "text-cyan-400" : "text-muted-foreground"}`}>
                  {data.ai_worker.running}
                </p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-muted-foreground text-[10px] uppercase">OK (24h)</p>
                <p className="text-emerald-400 text-lg font-bold">{data.ai_worker.completed_24h}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-muted-foreground text-[10px] uppercase">Error (24h)</p>
                <p className={`text-lg font-bold ${data.ai_worker.failed_24h > 0 ? "text-red-400" : "text-muted-foreground"}`}>
                  {data.ai_worker.failed_24h}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ JOB TRACKING (INDIVIDUAL) ══════════ */}
      {jobStats && (
        <div className="mb-6" data-testid="job-tracking-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white text-base font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              Job Tracking (Individual)
            </h2>
            {(jobStats.image_jobs?.stuck_jobs?.length > 0 || jobStats.recipe_jobs?.stuck_jobs?.length > 0) && (
              <Button
                onClick={cleanupStuckJobs}
                disabled={cleaningJobs}
                size="sm"
                variant="outline"
                className="gap-1.5 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                data-testid="cleanup-stuck-jobs-btn"
              >
                {cleaningJobs ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Limpiando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    Limpiar Stuck Jobs
                  </>
                )}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Image Jobs Card */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-500/15 text-indigo-400">
                  <Image className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-slate-300 text-sm font-semibold">Image Jobs</h3>
                  <p className="text-muted-foreground text-xs">Generación de imágenes IA</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-slate-800/50 rounded-lg p-2">
                  <p className="text-muted-foreground text-[10px] uppercase">Pendiente</p>
                  <p className="text-amber-400 text-lg font-bold">{jobStats.image_jobs?.pending || 0}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-2">
                  <p className="text-muted-foreground text-[10px] uppercase">Procesando</p>
                  <p className="text-cyan-400 text-lg font-bold">{jobStats.image_jobs?.processing || 0}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-2">
                  <p className="text-muted-foreground text-[10px] uppercase">OK (24h)</p>
                  <p className="text-emerald-400 text-lg font-bold">{jobStats.image_jobs?.completed_24h || 0}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-2">
                  <p className="text-muted-foreground text-[10px] uppercase">Error (24h)</p>
                  <p className="text-red-400 text-lg font-bold">{jobStats.image_jobs?.failed_24h || 0}</p>
                </div>
              </div>

              <div className="space-y-1.5 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Tiempo promedio</span>
                  <span className="text-slate-300 font-medium">
                    {jobStats.image_jobs?.avg_duration_seconds 
                      ? `${jobStats.image_jobs.avg_duration_seconds}s`
                      : "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total histórico</span>
                  <span className="text-slate-300 font-medium">
                    {jobStats.image_jobs?.total_all_time?.toLocaleString() || 0}
                  </span>
                </div>
              </div>

              {jobStats.image_jobs?.stuck_jobs?.length > 0 && (
                <div className="mt-3 p-2 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <p className="text-amber-400 text-xs font-medium flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {jobStats.image_jobs.stuck_jobs.length} job(s) stuck {'>'}10min
                  </p>
                  <div className="mt-1 space-y-0.5">
                    {jobStats.image_jobs.stuck_jobs.slice(0, 3).map((job) => (
                      <p key={job.job_id} className="text-muted-foreground text-[10px] truncate">
                        • {job.dish_name || job.job_id}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Recipe Jobs Card */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-500/15 text-emerald-400">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-slate-300 text-sm font-semibold">Recipe Jobs</h3>
                  <p className="text-muted-foreground text-xs">Generación de recetas IA</p>
                </div>
              </div>

              {jobStats.recipe_jobs?.total_all_time > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-muted-foreground text-[10px] uppercase">Pendiente</p>
                      <p className="text-amber-400 text-lg font-bold">{jobStats.recipe_jobs?.pending || 0}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-muted-foreground text-[10px] uppercase">Procesando</p>
                      <p className="text-cyan-400 text-lg font-bold">{jobStats.recipe_jobs?.processing || 0}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-muted-foreground text-[10px] uppercase">OK (24h)</p>
                      <p className="text-emerald-400 text-lg font-bold">{jobStats.recipe_jobs?.completed_24h || 0}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-muted-foreground text-[10px] uppercase">Error (24h)</p>
                      <p className="text-red-400 text-lg font-bold">{jobStats.recipe_jobs?.failed_24h || 0}</p>
                    </div>
                  </div>

                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Tiempo promedio</span>
                      <span className="text-slate-300 font-medium">
                        {jobStats.recipe_jobs?.avg_duration_seconds 
                          ? `${jobStats.recipe_jobs.avg_duration_seconds}s`
                          : "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Total histórico</span>
                      <span className="text-slate-300 font-medium">
                        {jobStats.recipe_jobs?.total_all_time?.toLocaleString() || 0}
                      </span>
                    </div>
                  </div>

                  {jobStats.recipe_jobs?.stuck_jobs?.length > 0 && (
                    <div className="mt-3 p-2 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                      <p className="text-amber-400 text-xs font-medium flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {jobStats.recipe_jobs.stuck_jobs.length} job(s) stuck {'>'}10min
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center mb-3">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground text-xs">No hay jobs de recetas aún</p>
                  <p className="text-foreground text-[10px] mt-1">
                    Se activará cuando uses generación individual de recetas
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════ PERFORMANCE ROW ══════════ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Event Loop Latency */}
        <div data-testid="event-loop-card"
          className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              (data?.event_loop_latency_ms || 0) > 50
                ? "bg-red-500/15 text-red-400"
                : (data?.event_loop_latency_ms || 0) > 10
                ? "bg-amber-500/15 text-amber-400"
                : "bg-emerald-500/15 text-emerald-400"
            }`}>
              <Gauge className="w-4 h-4" />
            </div>
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wide">Latencia Event Loop</p>
              <p className="text-white text-lg font-bold">
                {(data?.event_loop_latency_ms || 0).toFixed(2)} ms
              </p>
            </div>
          </div>
          <div className="space-y-2 mt-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Estado</span>
              {(data?.event_loop_latency_ms || 0) < 5 ? (
                <span className="text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Excelente
                </span>
              ) : (data?.event_loop_latency_ms || 0) < 50 ? (
                <span className="text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Aceptable
                </span>
              ) : (
                <span className="text-red-400 flex items-center gap-1">
                  <XCircle className="w-3 h-3" /> Bloqueado
                </span>
              )}
            </div>
            <p className="text-muted-foreground text-[10px]">
              Mide si el hilo principal de FastAPI puede responder. Valores {'>'} 50ms indican que operaciones
              CPU-bound están bloqueando las peticiones HTTP.
            </p>
            {/* Visual bar */}
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden mt-1">
              <div className={`h-full rounded-full transition-all duration-700 ${
                (data?.event_loop_latency_ms || 0) < 5 ? "bg-emerald-500" :
                (data?.event_loop_latency_ms || 0) < 50 ? "bg-amber-500" : "bg-red-500"
              }`} style={{ width: `${Math.min((data?.event_loop_latency_ms || 0) / 5, 100)}%` }} />
            </div>
          </div>
        </div>

        {/* Image Storage — Local + Cloud */}
        <div data-testid="image-storage-card"
          className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-500/15 text-indigo-400">
              <Image className="w-4 h-4" />
            </div>
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wide">Almacen de Imagenes</p>
              <p className="text-white text-lg font-bold">
                {data?.image_storage?.files || 0} archivos
              </p>
            </div>
          </div>

          {/* Local disk stats */}
          <div className="space-y-2 mt-3">
            <p className="text-muted-foreground text-[10px] uppercase tracking-wider font-semibold">Cache Local (Disco)</p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Archivos</span>
              <span className="text-slate-300 font-medium">{data?.image_storage?.local?.files ?? data?.image_storage?.files ?? 0}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Tamano total</span>
              <span className="text-slate-300 font-medium">{data?.image_storage?.local?.total_size_mb ?? data?.image_storage?.total_size_mb ?? 0} MB</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Tamano medio</span>
              <span className="text-slate-300 font-medium">{data?.image_storage?.local?.avg_size_kb ?? data?.image_storage?.avg_size_kb ?? 0} KB</span>
            </div>
            {data?.image_storage?.formats && Object.keys(data.image_storage.formats).length > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Formatos</span>
                <span className="text-slate-300 font-medium">
                  {Object.entries(data.image_storage.formats).map(([fmt, count]) =>
                    `${fmt.toUpperCase()}: ${count}`
                  ).join(", ")}
                </span>
              </div>
            )}
          </div>

          {/* Cloud Object Storage status */}
          {data?.image_storage?.cloud && (
            <div className="mt-4 pt-3 border-t border-slate-800 space-y-2">
              <p className="text-muted-foreground text-[10px] uppercase tracking-wider font-semibold">Object Storage (Nube)</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Proveedor</span>
                <span className="text-slate-300 font-medium">{data.image_storage.cloud.provider}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Estado</span>
                {data.image_storage.cloud.connected ? (
                  <span className="text-emerald-400 flex items-center gap-1 font-medium">
                    <CheckCircle2 className="w-3 h-3" /> Online
                  </span>
                ) : (
                  <span className="text-red-400 flex items-center gap-1 font-medium">
                    <XCircle className="w-3 h-3" /> Offline
                  </span>
                )}
              </div>
              {!data.image_storage.cloud.connected && data.image_storage.cloud.status && (
                <p className="text-red-400/70 text-[10px]">{data.image_storage.cloud.status}</p>
              )}
              <p className="text-muted-foreground text-[10px] mt-1">
                Las imagenes se persisten en la nube y se cachean localmente.
                Sobreviven a redespliegues del pod.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Log Severity Card */}
      <div className="mb-6" data-testid="log-severity-card">
        <h2 className="text-white text-base font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-muted-foreground" />
          Errores en Logs
        </h2>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <SeverityPill label="Critical" level="critical" count={data?.log_severity?.critical || 0} color="bg-rose-500" textColor="text-rose-400" testId="sev-critical" onClick={openSeverityDetail} />
            <SeverityPill label="Error" level="error" count={data?.log_severity?.error || 0} color="bg-red-500" textColor="text-red-400" testId="sev-error" onClick={openSeverityDetail} />
            <SeverityPill label="Warning" level="warning" count={data?.log_severity?.warning || 0} color="bg-amber-500" textColor="text-amber-400" testId="sev-warning" onClick={openSeverityDetail} />
            <SeverityPill label="Info" level="info" count={data?.log_severity?.info || 0} color="bg-sky-500" textColor="text-sky-400" testId="sev-info" onClick={openSeverityDetail} />
          </div>
          {(data?.log_severity?.critical > 0 || data?.log_severity?.error > 0) && (
            <p className="text-red-400/80 text-xs mt-4 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              Se detectaron errores — haz clic en una tarjeta para ver el detalle
            </p>
          )}
        </div>
      </div>

      {/* Severity Detail Drawer */}
      {sevDrawer && (
        <SeverityDrawer
          level={sevDrawer.level}
          lines={sevDrawer.lines}
          total={sevDrawer.total}
          loading={sevDrawer.loading}
          onClose={() => setSevDrawer(null)}
        />
      )}

      {/* Logs */}
      <div className="mb-6">
        <h2 className="text-white text-base font-semibold mb-3 flex items-center gap-2">
          <ScrollText className="w-4 h-4 text-muted-foreground" />
          Logs del Backend
        </h2>
        <LogViewer logs={data?.logs} />
      </div>

      {/* Alert Config */}
      <div className="mb-6" data-testid="alert-config-section">
        <h2 className="text-white text-base font-semibold mb-3 flex items-center gap-2">
          <Bell className="w-4 h-4 text-muted-foreground" />
          Alertas Automaticas por Email
        </h2>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-slate-300 text-sm font-medium">
                {alertConfig?.enabled ? "Alertas activadas" : "Alertas desactivadas"}
              </p>
              <p className="text-muted-foreground text-xs mt-0.5">
                Se enviara un email a todos los superadmins cuando se supere un umbral
              </p>
            </div>
            <button
              data-testid="toggle-alerts-btn"
              onClick={toggleAlerts}
              disabled={savingConfig}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                alertConfig?.enabled ? "bg-emerald-500" : "bg-slate-700"
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                alertConfig?.enabled ? "translate-x-6" : "translate-x-1"
              }`} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <ThresholdInput
              label="CPU"
              value={configDraft.cpu_percent}
              onChange={(v) => setConfigDraft(d => ({ ...d, cpu_percent: v }))}
              icon={<Cpu className="w-3.5 h-3.5" />}
              testId="threshold-cpu"
            />
            <ThresholdInput
              label="Memoria"
              value={configDraft.memory_percent}
              onChange={(v) => setConfigDraft(d => ({ ...d, memory_percent: v }))}
              icon={<MemoryStick className="w-3.5 h-3.5" />}
              testId="threshold-memory"
            />
            <ThresholdInput
              label="Disco"
              value={configDraft.disk_percent}
              onChange={(v) => setConfigDraft(d => ({ ...d, disk_percent: v }))}
              icon={<HardDrive className="w-3.5 h-3.5" />}
              testId="threshold-disk"
            />
          </div>

          <div className="flex justify-end mt-4">
            <Button
              data-testid="save-alert-config-btn"
              onClick={() => saveConfig()}
              disabled={savingConfig}
              size="sm"
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              {savingConfig ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Settings className="w-3.5 h-3.5 mr-1.5" />}
              Guardar umbrales
            </Button>
          </div>
        </div>
      </div>

      {/* Alert History */}
      <div className="mb-4" data-testid="alert-history-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white text-base font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-muted-foreground" />
            Historial de Alertas
          </h2>
          {alertHistory.length > 0 && (
            <Button
              data-testid="clear-alerts-btn"
              onClick={clearAlerts}
              disabled={clearingAlerts}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-red-400"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Limpiar
            </Button>
          )}
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          {alertHistory.length === 0 ? (
            <div className="text-center py-10">
              <BellOff className="w-7 h-7 text-foreground mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">No hay alertas registradas</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {alertHistory.map((alert) => (
                <div key={alert.alert_id} data-testid={`alert-${alert.alert_id}`}
                  className="px-5 py-3 flex items-center gap-4 hover:bg-slate-800/30 transition-colors">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    alert.severity === "critical" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"
                  }`}>
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-slate-200 text-sm font-medium">
                        {alert.label} al {alert.current_value}%
                      </p>
                      <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-full border ${
                        alert.severity === "critical"
                          ? "text-red-400 bg-red-500/10 border-red-500/20"
                          : "text-amber-400 bg-amber-500/10 border-amber-500/20"
                      }`}>
                        {alert.severity}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      Umbral: {alert.threshold}% | {new Date(alert.created_at).toLocaleString("es-ES")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusItem({ label, ok }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      {ok ? (
        <span className="flex items-center gap-1 text-emerald-400">
          <CheckCircle2 className="w-3 h-3" /> OK
        </span>
      ) : (
        <span className="flex items-center gap-1 text-red-400">
          <AlertTriangle className="w-3 h-3" /> Alerta
        </span>
      )}
    </div>
  );
}

function ThresholdInput({ label, value, onChange, icon, testId }) {
  return (
    <div data-testid={testId} className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-slate-400 text-xs font-medium">
        {icon} {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={50}
          max={99}
          value={value || 90}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="flex-1 h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4
                     [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-cyan-400
                     [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
        />
        <span className="text-white text-sm font-bold w-10 text-right">{value || 90}%</span>
      </div>
    </div>
  );
}

function SeverityPill({ label, level, count, color, textColor, testId, onClick }) {
  const hasIssues = count > 0 && (label === "Critical" || label === "Error");
  return (
    <div
      data-testid={testId}
      onClick={() => onClick(level)}
      className={`rounded-xl p-4 border transition-all cursor-pointer group ${
        hasIssues
          ? "bg-slate-800 border-red-500/30 hover:border-red-500/60"
          : "bg-slate-800/60 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-2.5 h-2.5 rounded-full ${color} ${hasIssues ? "animate-pulse" : "opacity-60"}`} />
        <span className="text-slate-400 text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${hasIssues ? textColor : "text-slate-300"}`}>
        {count.toLocaleString()}
      </p>
      <p className="text-muted-foreground text-[10px] mt-1 group-hover:text-slate-400 transition-colors">
        Ver detalle →
      </p>
    </div>
  );
}

const SEV_COLORS = {
  critical: { bg: "bg-rose-500/15", text: "text-rose-400", border: "border-rose-500/30", dot: "bg-rose-500" },
  error:    { bg: "bg-red-500/15",  text: "text-red-400",  border: "border-red-500/30",  dot: "bg-red-500" },
  warning:  { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30", dot: "bg-amber-500" },
  info:     { bg: "bg-sky-500/15",  text: "text-sky-400",  border: "border-sky-500/30",  dot: "bg-sky-500" },
};

function SeverityDrawer({ level, lines, total, loading, onClose }) {
  const scrollRef = useRef(null);
  const c = SEV_COLORS[level] || SEV_COLORS.info;

  useEffect(() => {
    if (!loading && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [loading]);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="severity-drawer">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-slate-950 border-l border-slate-800 flex flex-col
                      animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.bg} ${c.text}`}>
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-white text-sm font-bold uppercase tracking-wide">{level}</h3>
              <p className="text-muted-foreground text-xs">
                {total.toLocaleString()} lineas en total — mostrando las {Math.min(lines.length, 200)} mas recientes
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-severity-drawer"
            className="text-muted-foreground hover:text-white transition-colors p-1">
            <ChevronUp className="w-5 h-5 rotate-90" />
          </button>
        </div>

        {/* Lines */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
          </div>
        ) : lines.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <CheckCircle2 className="w-8 h-8 text-foreground mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">No hay lineas de nivel <span className={`font-bold ${c.text}`}>{level}</span></p>
            </div>
          </div>
        ) : (
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 font-mono text-xs space-y-0.5">
            {lines.map((entry, i) => (
              <div key={`sev-${i}-${entry?.text?.slice(0,20)}`} data-testid={`sev-line-${i}`}
                className="flex gap-2 py-1 px-2 rounded hover:bg-slate-900 transition-colors group">
                <span className="text-foreground select-none w-8 text-right flex-shrink-0">{i + 1}</span>
                <span className={`flex-shrink-0 text-muted-foreground text-[10px] mt-0.5 px-1.5 py-0.5 rounded
                  ${entry.file === "backend.err.log" ? "bg-red-500/10 text-red-400/60" : "bg-sky-500/10 text-sky-400/60"}`}>
                  {entry.file === "backend.err.log" ? "err" : "out"}
                </span>
                <span className="text-slate-400 break-all group-hover:text-slate-200 transition-colors">
                  {entry.text}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Metric History Modal ────────────────────────────────────────────────
const METRIC_CONFIG = {
  cpu: { label: "CPU", key: "cpu_percent", unit: "%", icon: Cpu, color: "#22c55e", warnColor: "#f59e0b", critColor: "#ef4444", warn: 50, crit: 80 },
  memory: { label: "Memoria", key: "memory_percent", unit: "%", icon: MemoryStick, color: "#3b82f6", warnColor: "#f59e0b", critColor: "#ef4444", warn: 60, crit: 85, secondaryKey: "memory_used_gb", secondaryUnit: "GB", secondaryLabel: "Uso" },
  disk: { label: "Disco", key: "disk_percent", unit: "%", icon: HardDrive, color: "#a855f7", warnColor: "#f59e0b", critColor: "#ef4444", warn: 70, crit: 90, secondaryKey: "disk_used_gb", secondaryUnit: "GB", secondaryLabel: "Uso" },
};

function MetricHistoryModal({ metric, onClose }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const cfg = METRIC_CONFIG[metric];
  const Icon = cfg.icon;

  useEffect(() => {
    setLoading(true);
    adminFetch(`/api/admin/system-metrics-history?hours=${hours}`)
      .then(d => setSnapshots(d.snapshots || []))
      .catch(() => toast.error("Error cargando historial"))
      .finally(() => setLoading(false));
  }, [hours]);

  const values = snapshots.map(s => s[cfg.key] || 0);
  const max = Math.max(...values, 1);
  const min = values.length > 0 ? Math.min(...values) : 0;
  const avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  const current = values.length > 0 ? values[values.length - 1] : 0;

  const getBarColor = (v) => {
    if (v > cfg.crit) return cfg.critColor;
    if (v > cfg.warn) return cfg.warnColor;
    return cfg.color;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="metric-history-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${cfg.color}20` }}>
              <Icon className="w-4.5 h-4.5" style={{ color: cfg.color }} />
            </div>
            <div>
              <h2 className="text-white text-lg font-bold">Historial de {cfg.label}</h2>
              <p className="text-muted-foreground text-xs">{snapshots.length} mediciones en las ultimas {hours}h</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Period selector */}
        <div className="px-5 py-3 border-b border-slate-800 flex gap-2">
          {[1, 6, 12, 24, 48, 168].map(h => (
            <button
              key={h}
              onClick={() => setHours(h)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                hours === h ? "bg-slate-700 text-white" : "text-muted-foreground hover:text-slate-300 hover:bg-slate-800"
              }`}
              data-testid={`period-${h}h`}
            >
              {h < 24 ? `${h}h` : h === 24 ? "24h" : h === 48 ? "2d" : "7d"}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: cfg.color }} />
            </div>
          ) : snapshots.length === 0 ? (
            <div className="text-center py-16">
              <Icon className="w-10 h-10 text-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No hay datos historicos para este periodo</p>
              <p className="text-foreground text-xs mt-1">Los datos se recopilan cada vez que visitas el Monitor</p>
            </div>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-[10px] uppercase">Actual</p>
                  <p className="text-xl font-bold" style={{ color: getBarColor(current) }}>{current.toFixed(1)}{cfg.unit}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-[10px] uppercase">Promedio</p>
                  <p className="text-xl font-bold text-slate-300">{avg.toFixed(1)}{cfg.unit}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-[10px] uppercase">Maximo</p>
                  <p className="text-xl font-bold" style={{ color: getBarColor(max) }}>{max.toFixed(1)}{cfg.unit}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-[10px] uppercase">Minimo</p>
                  <p className="text-xl font-bold text-slate-300">{min.toFixed(1)}{cfg.unit}</p>
                </div>
              </div>

              {/* Line Chart */}
              <TimeLineChart snapshots={snapshots} dataKey={cfg.key} color={cfg.color} warnColor={cfg.warnColor} critColor={cfg.critColor} warn={cfg.warn} crit={cfg.crit} unit={cfg.unit} secondaryKey={cfg.secondaryKey} secondaryUnit={cfg.secondaryUnit} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TimeLineChart({ snapshots, dataKey, color, warnColor, critColor, warn, crit, unit, secondaryKey, secondaryUnit }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const W = 600, H = 200, PAD_L = 45, PAD_R = 15, PAD_T = 15, PAD_B = 30;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const values = snapshots.map(s => s[dataKey] || 0);
  const yMax = Math.max(100, ...values);
  const yMin = 0;

  const toX = (i) => PAD_L + (i / Math.max(snapshots.length - 1, 1)) * chartW;
  const toY = (v) => PAD_T + chartH - ((v - yMin) / (yMax - yMin)) * chartH;

  // Build path
  const linePath = snapshots.map((s, i) => {
    const x = toX(i);
    const y = toY(s[dataKey] || 0);
    return `${i === 0 ? "M" : "L"} ${x} ${y}`;
  }).join(" ");

  // Gradient fill path
  const areaPath = linePath + ` L ${toX(snapshots.length - 1)} ${PAD_T + chartH} L ${PAD_L} ${PAD_T + chartH} Z`;

  // Y-axis labels
  const yTicks = [0, 25, 50, 75, 100];

  // X-axis labels (show ~6 time labels)
  const xLabelStep = Math.max(1, Math.ceil(snapshots.length / 6));
  const xLabels = snapshots
    .map((s, i) => ({ i, ts: new Date(s.timestamp) }))
    .filter((_, idx) => idx % xLabelStep === 0 || idx === snapshots.length - 1);

  const getPointColor = (v) => {
    if (v > crit) return critColor;
    if (v > warn) return warnColor;
    return color;
  };

  const hovered = hoveredIdx !== null ? snapshots[hoveredIdx] : null;
  const hoveredVal = hovered ? (hovered[dataKey] || 0) : 0;

  return (
    <div className="bg-slate-800/30 rounded-xl p-4" data-testid="metric-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" onMouseLeave={() => setHoveredIdx(null)}>
        <defs>
          <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {yTicks.map(v => (
          <g key={v}>
            <line x1={PAD_L} y1={toY(v)} x2={W - PAD_R} y2={toY(v)} stroke="#1e293b" strokeWidth="1" />
            <text x={PAD_L - 6} y={toY(v) + 3} textAnchor="end" fill="#475569" fontSize="9">{v}%</text>
          </g>
        ))}

        {/* Threshold lines */}
        <line x1={PAD_L} y1={toY(warn)} x2={W - PAD_R} y2={toY(warn)} stroke={warnColor} strokeWidth="1" strokeDasharray="4 3" opacity="0.4" />
        <line x1={PAD_L} y1={toY(crit)} x2={W - PAD_R} y2={toY(crit)} stroke={critColor} strokeWidth="1" strokeDasharray="4 3" opacity="0.4" />
        <text x={W - PAD_R + 2} y={toY(warn) + 3} fill={warnColor} fontSize="7" opacity="0.6">{warn}%</text>
        <text x={W - PAD_R + 2} y={toY(crit) + 3} fill={critColor} fontSize="7" opacity="0.6">{crit}%</text>

        {/* X-axis labels */}
        {xLabels.map(({ i, ts }) => (
          <text key={`t-${i}-${snapshots[i]?.timestamp || i}`} x={toX(i)} y={H - 5} textAnchor="middle" fill="#475569" fontSize="8">
            {ts.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
          </text>
        ))}

        {/* Area fill */}
        {snapshots.length > 1 && <path d={areaPath} fill={`url(#grad-${dataKey})`} />}

        {/* Line */}
        {snapshots.length > 1 && (
          <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        )}

        {/* Data points */}
        {snapshots.map((s, i) => {
          const v = s[dataKey] || 0;
          const x = toX(i);
          const y = toY(v);
          const isHovered = hoveredIdx === i;
          return (
            <g key={`dp-${i}-${s?.[cfg.key] || i}`}>
              {/* Invisible hitbox */}
              <rect
                x={x - (chartW / snapshots.length / 2)}
                y={PAD_T}
                width={chartW / snapshots.length}
                height={chartH}
                fill="transparent"
                onMouseEnter={() => setHoveredIdx(i)}
              />
              {/* Dot */}
              <circle cx={x} cy={y} r={isHovered ? 5 : 2.5} fill={getPointColor(v)}
                stroke={isHovered ? "#fff" : "none"} strokeWidth={isHovered ? 2 : 0}
                className="transition-all duration-150" />
            </g>
          );
        })}

        {/* Hover crosshair + tooltip */}
        {hoveredIdx !== null && hovered && (
          <g>
            <line x1={toX(hoveredIdx)} y1={PAD_T} x2={toX(hoveredIdx)} y2={PAD_T + chartH}
              stroke="#475569" strokeWidth="1" strokeDasharray="3 3" />
            <rect x={toX(hoveredIdx) - 50} y={toY(hoveredVal) - 30} width="100" height="22" rx="4"
              fill="#0f172a" stroke="#334155" strokeWidth="1" />
            <text x={toX(hoveredIdx)} y={toY(hoveredVal) - 15} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">
              {hoveredVal.toFixed(1)}{unit} — {new Date(hovered.timestamp).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
            </text>
          </g>
        )}
      </svg>

      {/* Secondary data table below chart */}
      {secondaryKey && (
        <div className="mt-3 pt-3 border-t border-slate-800/50">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-2">
            <span className="uppercase tracking-wider font-semibold">Ultimas mediciones</span>
          </div>
          <div className="max-h-32 overflow-y-auto space-y-0.5">
            {[...snapshots].reverse().slice(0, 20).map((s, i) => {
              const v = s[dataKey] || 0;
              const ts = new Date(s.timestamp);
              return (
                <div key={`stat-${i}-${s?.timestamp || i}`} className="flex items-center gap-3 text-[10px] py-1 px-2 rounded hover:bg-slate-800/40">
                  <span className="text-muted-foreground w-20">{ts.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="font-semibold w-10" style={{ color: getPointColor(v) }}>{v.toFixed(1)}{unit}</span>
                  <span className="text-muted-foreground">{(s[secondaryKey] || 0).toFixed(2)} {secondaryUnit}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
